import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { api } from '../App';
import { BookOpen, FileText, LogOut, CheckCircle2, Clock } from 'lucide-react';

export default function StudentDashboard({ user, logout, setUser }) {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [assessmentsRes, reportsRes] = await Promise.all([
        api.get('/assessments'),
        api.get('/reports')
      ]);
      setAssessments(assessmentsRes.data);
      setReports(reportsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const hasCompletedAssessment = assessments.some(a => a.status === 'completed');
  const inProgressAssessment = assessments.find(a => a.status === 'in_progress');

  const handleStopImpersonation = async () => {
    try {
      const response = await api.post('/impersonate/stop');
      
      const { access_token, user: adminUser } = response.data;
      localStorage.setItem('access_token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(adminUser);
      
      toast.success('Süper admin hesabınıza döndünüz');
      navigate('/');
    } catch (error) {
      console.error('Stop impersonation failed:', error);
      toast.error('Oturum sonlandırılamadı');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-emerald-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Mizaç360</h1>
              <p className="text-sm text-slate-600">Öğrenci Paneli</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {user.impersonation?.active && (
              <Badge className="bg-orange-100 text-orange-700 animate-pulse">
                Taklit Modu: {user.impersonation.admin_name}
              </Badge>
            )}
            <span className="text-sm text-slate-600" data-testid="user-name">Merhaba, {user.name}</span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/coach')}
              className="bg-gradient-to-r from-blue-50 to-emerald-50 border-blue-200 hover:bg-blue-100"
            >
              💬 Koçumla Konuş
            </Button>
            {user.impersonation?.active ? (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleStopImpersonation}
                className="bg-orange-50 border-orange-200"
              >
                Taklidi Durdur
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Çıkış
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8 animate-fade-in">
          <h2 className="text-3xl font-bold text-slate-800 mb-2">Panelime Hoş Geldin!</h2>
          <p className="text-slate-600">Mizaç profilini keşfet ve kendini daha iyi tanı</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Assessment Card */}
          <Card className="shadow-lg border-none card-hover" data-testid="assessment-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-blue-600" />
                Mizaç Envanteri
              </CardTitle>
              <CardDescription>
                {hasCompletedAssessment 
                  ? 'Envanteri tamamladın! Raporunu görebilirsin.' 
                  : 'Kendini keşfetmek için mizaç envanterini doldur'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {hasCompletedAssessment ? (
                <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  <span className="text-emerald-700 font-medium">Tamamlandı</span>
                </div>
              ) : inProgressAssessment ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                    <Clock className="w-5 h-5 text-blue-600" />
                    <span className="text-blue-700 text-sm">Devam eden envanter var</span>
                  </div>
                  <Button 
                    className="w-full bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700"
                    onClick={() => navigate(`/assessment/${inProgressAssessment.id}`)}
                    data-testid="continue-assessment-btn"
                  >
                    Devam Et
                  </Button>
                </div>
              ) : (
                <Button 
                  className="w-full bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700"
                  onClick={() => navigate('/assessment/intro')}
                  data-testid="start-assessment-btn"
                >
                  Envanteri Başlat
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Profile Completion Card */}
          <Card className="shadow-lg border-none card-hover" data-testid="profile-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                Profil Tamamlama
              </CardTitle>
              <CardDescription>
                Profilini tamamladıkça seni daha iyi tanırız
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-600">Tamamlanma Oranı</span>
                  <span className="font-semibold text-slate-800">{hasCompletedAssessment ? '100' : '40'}%</span>
                </div>
                <Progress value={hasCompletedAssessment ? 100 : 40} className="h-2" />
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span className="text-slate-600">Hesap oluşturuldu</span>
                </div>
                <div className="flex items-center gap-2">
                  {hasCompletedAssessment ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
                  )}
                  <span className="text-slate-600">Mizaç envanteri</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Reports Section */}
        <Card className="shadow-lg border-none" data-testid="reports-section">
          <CardHeader>
            <CardTitle>Raporlarım</CardTitle>
            <CardDescription>
              {reports.length > 0 
                ? 'Mizaç profiline göre hazırlanmış kişisel raporların' 
                : 'Henuz rapor bulunmuyor. Envanteri tamamladıktan sonra raporun hazırlanacak.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : reports.length > 0 ? (
              <div className="space-y-3">
                {reports.map((report) => (
                  <div
                    key={report.id}
                    className="flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                    onClick={() => navigate(`/report/${report.id}`)}
                    data-testid={`report-item-${report.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-emerald-600 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">Öğrenci Mizaç Raporu</p>
                        <p className="text-sm text-slate-600">
                          {new Date(report.created_at).toLocaleDateString('tr-TR')}
                        </p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">Görüntüle</Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Henuz rapor bulunmuyor</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}